import type {
  ProductLineItem,
  ShippingLineItem,
} from '@trmediaab/zebra-types/trhub';

import type { CheckoutState } from '../state';
import { getSiteBaseUrl, getSiteLinkText, pathToSiteUrl } from './siteUtils';

export function getProductLineItems(order: CheckoutState['order']) {
  if (order == null || order.lineItems == null) {
    return null;
  }

  const lineItems = order.lineItems.filter(
    (lineItem): lineItem is ProductLineItem => lineItem.type === 'product',
  );

  return lineItems;
}

export function getShippingLineItem(order: CheckoutState['order']) {
  if (order == null || order.lineItems == null) {
    return null;
  }

  const shippingLineItem = order.lineItems.find(
    (lineItem): lineItem is ShippingLineItem => lineItem.type === 'shipping',
  );

  return shippingLineItem || null;
}

export function isShippingOrder(order: CheckoutState['order']) {
  return getShippingLineItem(order) != null;
}

export function isBetTipProduct(order: CheckoutState['order']) {
  const [lineItem] = getProductLineItems(order) || [];

  if (lineItem == null || lineItem.product == null) {
    return null;
  }

  const { type } = lineItem.product;

  return type === 'bettip';
}

export function getProductSite(order: CheckoutState['order']) {
  const [lineItem] = getProductLineItems(order) || [];

  if (lineItem == null || lineItem.product == null) {
    return null;
  }

  return lineItem.product.site;
}

function makeLinkTuple(url: string | null, text: string | null) {
  if (url == null || text == null) {
    return null;
  }
  return [url, text] as const;
}

export function getProductLink(
  order: CheckoutState['order'],
  returnUrl?: string,
  returnUrlText?: string,
) {
  const [lineItem] = getProductLineItems(order) || [];

  if (lineItem == null || lineItem.product == null) {
    return null;
  }

  const { sku, type, site } = lineItem.product;

  // Einars hästkalender - no call to action
  if (sku === 'MC-EIN') {
    return null;
  }

  // Travronden vinternumret - send user back to Travronden home page
  if (sku === 'MC-TVI') {
    return makeLinkTuple(
      getSiteBaseUrl('travronden'),
      returnUrlText || 'Till Travronden',
    );
  }

  // Bet tip - returnUrl is expected to be set if checkout is on
  // the product site. Otherwise, or if we're on a landing page or
  // campaign page, we link to a url on the product site that will
  // lookup and redirect to the bet tip.
  if (type === 'bettip') {
    return makeLinkTuple(
      returnUrl != null
        ? pathToSiteUrl(site, returnUrl)
        : pathToSiteUrl(site, `/product-redirect/${sku}`),
      returnUrlText || 'Läs tipset',
    );
  }

  // A return url was set as prop to the checkout component
  if (returnUrl != null) {
    return makeLinkTuple(
      pathToSiteUrl(site, returnUrl),
      returnUrlText || 'Tillbaka',
    );
  }

  // Finally, link back to the product's home page
  return makeLinkTuple(
    getSiteBaseUrl(site),
    returnUrlText || getSiteLinkText(site),
  );
}
